import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportReportsPanel = () => {
  const [selectedReportType, setSelectedReportType] = useState('comprehensive');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [dateRange, setDateRange] = useState('month');
  const [selectedCompanies, setSelectedCompanies] = useState(['all']);
  const [isGenerating, setIsGenerating] = useState(false);

  const reportTypes = [
    {
      id: 'comprehensive',
      name: 'Reporte Integral',
      description: 'Análisis completo del sistema incluyendo todas las métricas',
      icon: 'FileText',
      estimatedSize: '2.5 MB'
    },
    {
      id: 'performance',
      name: 'Rendimiento del Sistema',
      description: 'Métricas de rendimiento, tiempos de respuesta y recursos',
      icon: 'Zap',
      estimatedSize: '1.2 MB'
    },
    {
      id: 'company-activity',
      name: 'Actividad por Empresa',
      description: 'Análisis detallado de la actividad de cada empresa cliente',
      icon: 'Building2',
      estimatedSize: '1.8 MB'
    },
    {
      id: 'employee-engagement',
      name: 'Participación de Empleados',
      description: 'Métricas de acceso y descarga de empleados',
      icon: 'Users',
      estimatedSize: '1.5 MB'
    },
    {
      id: 'error-analysis',
      name: 'Análisis de Errores',
      description: 'Reporte detallado de errores y problemas del sistema',
      icon: 'AlertTriangle',
      estimatedSize: '0.8 MB'
    },
    {
      id: 'sat-connection',
      name: 'Conexión SAT',
      description: 'Estado de conexión y certificados FIEL',
      icon: 'Shield',
      estimatedSize: '0.6 MB'
    }
  ];

  const formatOptions = [
    { id: 'pdf', name: 'PDF', icon: 'FileText', description: 'Formato ideal para presentaciones' },
    { id: 'excel', name: 'Excel', icon: 'Table', description: 'Para análisis de datos adicional' },
    { id: 'csv', name: 'CSV', icon: 'Database', description: 'Datos en formato de tabla' }
  ];

  const dateRangeOptions = [
    { id: 'today', name: 'Hoy', description: 'Datos del día actual' },
    { id: 'week', name: 'Esta Semana', description: 'Últimos 7 días' },
    { id: 'month', name: 'Este Mes', description: 'Mes actual completo' },
    { id: 'quarter', name: 'Trimestre', description: 'Últimos 3 meses' },
    { id: 'year', name: 'Este Año', description: 'Año actual completo' },
    { id: 'custom', name: 'Personalizado', description: 'Seleccionar fechas específicas' }
  ];

  const companies = [
    { id: 'all', name: 'Todas las Empresas' },
    { id: 'constructora-abc', name: 'Constructora ABC' },
    { id: 'servicios-xyz', name: 'Servicios XYZ' },
    { id: 'comercial-def', name: 'Comercial DEF' },
    { id: 'industrias-ghi', name: 'Industrias GHI' },
    { id: 'logistica-jkl', name: 'Logística JKL' },
    { id: 'tecnologia-mno', name: 'Tecnología MNO' }
  ];

  const recentReports = [
    {
      id: 1,
      name: 'Reporte Integral - Septiembre 2024',
      type: 'comprehensive',
      format: 'pdf',
      size: '2.3 MB',
      generatedAt: new Date(Date.now() - 3600000),
      downloadCount: 5
    },
    {
      id: 2,
      name: 'Análisis de Errores - Semana 36',
      type: 'error-analysis',
      format: 'excel',
      size: '0.9 MB',
      generatedAt: new Date(Date.now() - 86400000),
      downloadCount: 2
    },
    {
      id: 3,
      name: 'Actividad Empresas - Agosto 2024',
      type: 'company-activity',
      format: 'pdf',
      size: '1.7 MB',
      generatedAt: new Date(Date.now() - 172800000),
      downloadCount: 8
    }
  ];

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    setIsGenerating(false);
    
    // In a real application, this would trigger the actual report generation and download
    alert('Reporte generado exitosamente. La descarga comenzará automáticamente.');
  };

  const handleCompanySelection = (companyId) => {
    if (companyId === 'all') {
      setSelectedCompanies(['all']);
    } else {
      const newSelection = selectedCompanies?.includes('all') 
        ? [companyId]
        : selectedCompanies?.includes(companyId)
          ? selectedCompanies?.filter(id => id !== companyId)
          : [...selectedCompanies, companyId];
      
      setSelectedCompanies(newSelection?.length === 0 ? ['all'] : newSelection);
    }
  };

  const formatTimestamp = (timestamp) => {
    return timestamp?.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReportTypeIcon = (type) => {
    const reportType = reportTypes?.find(rt => rt?.id === type);
    return reportType ? reportType?.icon : 'FileText';
  };

  const getFormatIcon = (format) => {
    const formatOption = formatOptions?.find(fo => fo?.id === format);
    return formatOption ? formatOption?.icon : 'FileText';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Exportar Reportes</h2>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>Los reportes se generan con formato mexicano estándar</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Report Type Selection */}
          <div className="bg-card rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4">Tipo de Reporte</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reportTypes?.map((type) => (
                <button
                  key={type?.id}
                  onClick={() => setSelectedReportType(type?.id)}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    selectedReportType === type?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon 
                      name={type?.icon} 
                      size={20} 
                      className={selectedReportType === type?.id ? 'text-primary' : 'text-muted-foreground'} 
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{type?.name}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{type?.description}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Tamaño estimado: {type?.estimatedSize}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Format and Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Format Selection */}
            <div className="bg-card rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4">Formato</h3>
              <div className="space-y-2">
                {formatOptions?.map((format) => (
                  <button
                    key={format?.id}
                    onClick={() => setSelectedFormat(format?.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      selectedFormat === format?.id
                        ? 'border-primary bg-primary/10' :'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon 
                        name={format?.icon} 
                        size={18} 
                        className={selectedFormat === format?.id ? 'text-primary' : 'text-muted-foreground'} 
                      />
                      <div>
                        <p className="font-medium text-foreground">{format?.name}</p>
                        <p className="text-sm text-muted-foreground">{format?.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Selection */}
            <div className="bg-card rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4">Período</h3>
              <div className="space-y-2">
                {dateRangeOptions?.map((range) => (
                  <button
                    key={range?.id}
                    onClick={() => setDateRange(range?.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      dateRange === range?.id
                        ? 'border-primary bg-primary/10' :'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-foreground">{range?.name}</p>
                      <p className="text-sm text-muted-foreground">{range?.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Company Selection */}
          <div className="bg-card rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4">Empresas a Incluir</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {companies?.map((company) => (
                <button
                  key={company?.id}
                  onClick={() => handleCompanySelection(company?.id)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    selectedCompanies?.includes(company?.id)
                      ? 'border-primary bg-primary/10' :'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      selectedCompanies?.includes(company?.id)
                        ? 'border-primary bg-primary' :'border-muted-foreground'
                    }`}>
                      {selectedCompanies?.includes(company?.id) && (
                        <Icon name="Check" size={12} className="text-primary-foreground" />
                      )}
                    </div>
                    <span className="font-medium text-foreground">{company?.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Generate Report */}
          <div className="bg-card rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4">Generar Reporte</h3>
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name={getReportTypeIcon(selectedReportType)} size={16} className="text-primary" />
                  <span className="text-sm font-medium">
                    {reportTypes?.find(rt => rt?.id === selectedReportType)?.name}
                  </span>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name={getFormatIcon(selectedFormat)} size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Formato {selectedFormat?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {dateRangeOptions?.find(dr => dr?.id === dateRange)?.name}
                  </span>
                </div>
              </div>

              <Button
                onClick={handleGenerateReport}
                loading={isGenerating}
                iconName="Download"
                iconPosition="left"
                fullWidth
                className="mt-4"
              >
                {isGenerating ? 'Generando...' : 'Generar y Descargar'}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                El reporte se descargará automáticamente una vez generado
              </p>
            </div>
          </div>

          {/* Recent Reports */}
          <div className="bg-card rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4">Reportes Recientes</h3>
            <div className="space-y-3">
              {recentReports?.map((report) => (
                <div key={report?.id} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-2">
                      <Icon 
                        name={getReportTypeIcon(report?.type)} 
                        size={16} 
                        className="text-primary mt-0.5" 
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {report?.name}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Icon name={getFormatIcon(report?.format)} size={12} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{report?.size}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestamp(report?.generatedAt)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Download"
                      className="h-8 w-8"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {report?.downloadCount} descargas
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      iconName="Share"
                      iconPosition="left"
                    >
                      Compartir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportReportsPanel;